import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import Image from 'next/image';
import {
  CheckBadgeIcon,
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  Cog6ToothIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useAtomValue, useSetAtom } from 'jotai';

import { AccordionV2, Alert, ButtonV2, ProductReviewCard, Title } from '@restworld/ui-ds';
import { Badge } from '@restworld/ui-ds-base';
import {
  CheckmarkIcon,
  customImageLoader,
  getAssetSrc,
  HeadphoneIcon,
  useIsFreemiumUser,
} from '@restworld/utils-common';

import {
  productAdvantages,
  productFeatures,
  productReviews,
  productWorkingSteps,
} from './product-description.constants';
import { ServiceDescriptionBox } from './service-description-box';
import { showContactRestWorldManagerModalAtom } from '../restaurants/employer-restaurants-filters/employer-restaurants.atoms';
import { useBuySlot, useProductFunctionalitiesData } from './product-description.hooks';
import {
  useIsShowOrganisationQueryLoading,
  useShowOrganizationData,
} from '../organisations/hooks/use-show-organisation-query';
import { useV2Router } from '../dashboard/hooks/use-v2-router';
import {
  activeSelectionSubscriptionItemAtom,
  onOrganizationCompanySelectToggleAtom,
  slotNumberAtom,
} from './subscription-checkout';
import { useIsEmployerAnAdmin } from '@restworld/feature-authentication';
import { isEmployerNoPermissionToBuySlotModalOpenAtom } from '../employer-companies/modals/employer-no-permission-to-buy-slot-modal';
import { globalDiscountAtom } from '.';

export const ProductDescriptionSectionTitle = ({ title }: { title: string }) => (
  <Title fontSize="base" fontWeight="semibold" content={title} />
);

export const DefinitionWrapper = tw.div`flex flex-row gap-1 flex-wrap`;

export const ProductStep = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div tw="self-stretch justify-start gap-2 inline-flex flex-col md:(flex-row items-center)">
    <CheckBadgeIcon className="w-4 h-4 text-purple-500 self-start md:self-center" />
    <div tw="grow shrink basis-0">
      <Title
        fontWeight="semibold"
        textColor="neutral-700"
        content={title}
        twStyle={subtitle ? tw`` : tw`font-normal`}
      />
      {subtitle && <Title fontWeight="normal" textColor="neutral-700" content={subtitle} />}
    </div>
  </div>
);

export const FlexibleSlotAdvantageCard = ({
  title,
  illustration,
  subtitle,
}: {
  title: string;
  subtitle: string;
  illustration: React.ReactNode;
}) => (
  <div tw="min-h-[120px] p-5 bg-purple-50 rounded-2xl justify-start items-center gap-5 inline-flex">
    <div tw="grow shrink basis-0 flex-col justify-start items-start gap-4 inline-flex">
      <div tw="self-stretch">
        <Title textColor="purple-700" fontWeight="semibold" content={title} twStyle={tw`text-sm`} />
        <Title twStyle={tw`text-sm`} textColor="neutral-700" content={subtitle} />
      </div>
    </div>
    {illustration}
  </div>
);

export const ProductDescriptionSteps = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex-col justify-start items-start gap-5 inline-flex">
      {productWorkingSteps.map((step, index) => (
        <ProductStep key={index} title={t(step)} />
      ))}
    </div>
  );
};

export const ProductDescriptionFeatures = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex-col justify-start items-start gap-5 inline-flex">
      {productFeatures.map((feature, index) => (
        <ProductStep key={index} title={t(feature)} />
      ))}
    </div>
  );
};

export const HeaderSection = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex flex-col gap-4 items-center justify-center text-center">
      <Title fontSize="superSuperLarge" fontWeight="semibold" content={t('choose_the_right_plan_for_you')} />
      <Badge variant="restworld">
        <Title
          textColor="purple-700"
          twStyle={tw`text-sm leading-[16.94px]`}
          fontSize="small"
          fontWeight="medium"
          content={t('paid_service')}
        />
      </Badge>
      {/* <Title
        textColor="neutral-700"
        twStyle={tw`text-2xl`}
        fontWeight="semibold"
        content={t('find_the_right_candidate_without_wasting_time')}
      />
      <Title
        textColor="neutral-700"
        twStyle={tw`text-lg italic relative -top-4`}
        fontWeight="light"
        content={t('the_easiest_and_most_professional_way_to_hire_in_the_restaurant_world')}
      />
      <ProductReviewCard {...productReviews[0]} /> */}
    </div>
  );
};

export const FlexibleSlotSection = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex flex-col gap-4">
      <ProductDescriptionSectionTitle title={t('what_are_restworld_slots')} />
      <Alert
        text={t('it_is_a_credit_that_allows_you_to_activate_a_personnel_search_with_restworld')}
        variant="restworld"
      />
      <div tw="text-neutral-700 text-base flex flex-col gap-5">
        <span tw="block">{t('slots_are_the_easiest_and_fastest_key')}</span>
        <span>{t('tell_us_what_you_are_looking_for_key')}</span>
      </div>
    </div>
  );
};

export function FunctionalitiesSection() {
  const { t } = useTranslation('product-description-page');
  const functionalitiesData = useProductFunctionalitiesData();

  return (
    <section tw="border border-neutral-40 rounded-2xl p-4 lg:(p-6)">
      <div tw="flex font-semibold text-base text-neutral-700 border-b py-6">
        <div tw="flex-[2]">{t('functionality')}</div>
        <div tw="flex-1 text-center">{t('free_showcase')}</div>
        <div tw="flex-1 text-center">{t('search_with_slot')}</div>
      </div>
      {functionalitiesData.map((functionality, i) => (
        <FunctionalityRow {...functionality} key={i} />
      ))}
    </section>
  );
}

function FunctionalityRow({ noBottomBorder, freeTier, paidTier, content }: FunctionalityRowProps) {
  return (
    <FunctionalityRowWrapper noBottomBorder={noBottomBorder}>
      <div tw="flex-[2]">{content}</div>
      <div tw="flex-1 flex justify-center">
        {freeTier ? <CheckmarkIcon /> : <XMarkIcon tw="h-6 w-6 text-red-500" />}
      </div>
      <div tw="flex-1 text-center flex justify-center">
        {paidTier ? <CheckmarkIcon /> : <XMarkIcon tw="h-6 w-6 text-red-500" />}
      </div>
    </FunctionalityRowWrapper>
  );
}

export const HowItWorksSection = () => {
  const { t } = useTranslation('product-description-page');

  return (
    <div tw="flex flex-col gap-12 items-center text-center">
      {/* <ProductDescriptionSectionTitle title={t('how_does_our_service_work')} /> */}
      <Title
        content="Dal tuo annuncio al candidato giusto: ecco cosa succede utilizzando il nostro servizio Premium"
        fontSize="superLarge"
        fontWeight="semibold"
      />

      <ServiceDescriptionBox
        variant="job-preview"
        title={t('how_it_works_step_1_title')}
        description={t('how_it_works_step_1_description')}
        cardPosition="right"
      />

      <ServiceDescriptionBox
        variant="publishing"
        title={t('how_it_works_step_2_title')}
        description={t('how_it_works_step_2_description')}
        cardPosition="left"
      />

      <ServiceDescriptionBox
        variant="candidate-screening"
        title={t('how_it_works_step_3_title')}
        description={t('how_it_works_step_3_description')}
        cardPosition="right"
      />

      <ServiceDescriptionBox
        variant="dashboard"
        title={t('how_it_works_step_4_title')}
        description={t('how_it_works_step_4_description')}
        cardPosition="left"
      />
    </div>
  );
};

export function ActivateFirstSlotBanner() {
  const { t } = useTranslation('product-description-page');
  const { buySlot } = useBuySlot();
  const organizationData = useShowOrganizationData();
  const isOrganisationDataLoading = useIsShowOrganisationQueryLoading();
  const router = useV2Router();
  const toggleOrganizationCompanySelect = useSetAtom(onOrganizationCompanySelectToggleAtom);
  const setSlotNumber = useSetAtom(slotNumberAtom);
  const setIsEmployerNoPermissionToBuySlotModalOpen = useSetAtom(isEmployerNoPermissionToBuySlotModalOpenAtom);
  const isAdmin = useIsEmployerAnAdmin();
  const setItemSelected = useSetAtom(activeSelectionSubscriptionItemAtom);
  const globalDiscount = useAtomValue(globalDiscountAtom);
  const isFreemiumUser = useIsFreemiumUser();

  const handleCheckout = React.useCallback(() => {
    if (!isAdmin) {
      setIsEmployerNoPermissionToBuySlotModalOpen(true);
      return;
    }
    setSlotNumber(1);
    if (isFreemiumUser) setItemSelected('trial');
    else setItemSelected('custom');

    if (!organizationData?.companies?.length) {
      router.push('/employer/purchase/create-company');
    } else if (organizationData?.companies?.length === 1) {
      buySlot({ slots: 1, companyId: organizationData?.companies?.[0]?.id });
    } else {
      toggleOrganizationCompanySelect(true);
    }
  }, [
    buySlot,
    isAdmin,
    organizationData?.companies,
    router,
    setIsEmployerNoPermissionToBuySlotModalOpen,
    setSlotNumber,
    toggleOrganizationCompanySelect,
    setItemSelected,
    isFreemiumUser,
  ]);

  return (
    <div tw="relative bg-neutral-700 flex flex-col items-center justify-center gap-4 rounded-2xl py-6 px-4 min-h-[192px] overflow-hidden">
      <svg
        tw="absolute opacity-10 pointer-events-none"
        style={{
          width: '330.933px',
          height: '281.833px',
          transform: 'rotate(-30deg)',
          left: '-42.111px',
          top: '-182.491px',
        }}
        width="386"
        height="96"
        viewBox="0 0 386 96"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.1"
          d="M489.471 -364.982L-83.7227 -34.0488L198.111 454.101L771.304 123.168L489.471 -364.982Z"
          fill="url(#pattern0_cta_banner)"
        />
        <defs>
          <pattern id="pattern0_cta_banner" patternContentUnits="objectBoundingBox" width="1" height="1">
            <rect width="100%" height="100%" fill="white" />
          </pattern>
        </defs>
      </svg>
      {(globalDiscount?.discount ?? 0) > 0 && (
        <Badge variant="tertiary">{`Sconto di benvenuto: -${globalDiscount?.discount ?? 0}€`}</Badge>
      )}
      <div tw="text-purple-50 text-2xl font-praktikal-bold text-center relative z-10">
        {t('activate_your_first_slot_now')}
      </div>
      <ButtonV2 onClick={handleCheckout} disabled={isOrganisationDataLoading} loading={isOrganisationDataLoading}>
        {`${isFreemiumUser ? t('buy_one_slot_trial') : t('buy_one_slot')} + ${t('vat')}`}
      </ButtonV2>
    </div>
  );
}

// Removed unused PriceText component

export const AdvantagesSection = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex flex-col gap-4">
      <ProductDescriptionSectionTitle title={t('product_description_benefit_title')} />
      <div tw="grid grid-cols-1 gap-4 md:grid-cols-2">
        {productAdvantages.map(({ title, subtitle, illustration }) => (
          <FlexibleSlotAdvantageCard key={title} title={t(title)} subtitle={t(subtitle)} illustration={illustration} />
        ))}
      </div>
    </div>
  );
};

export const IncludedFeaturesSection = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex flex-col gap-4">
      <ProductDescriptionSectionTitle title={t('what_is_included_in_the_cost_of_the_service')} />
      <div tw="flex-col justify-start items-start gap-5 inline-flex">
        {productFeatures.map((feature, index) => (
          <ProductStep key={index} title={t(feature)} />
        ))}
      </div>
    </div>
  );
};

export const TrustedCompaniesSection = () => {
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="flex flex-col gap-4 items-center text-center">
      <Title
        fontSize="superLarge"
        fontWeight="semibold"
        textColor="neutral-700"
        content={t('those_who_work_well_choose_well')}
      />
      {/* <ProductDescriptionSectionTitle title={t('they_have_already_chosen_restworld')} /> */}
      <Title
        fontSize="base"
        fontWeight="normal"
        textColor="neutral-700"
        content={t('from_big_brands_key')}
        // twStyle={tw`relative -top-4`}
      />

      <div tw="relative w-full min-h-[74px] grid place-items-center">
        <Image
          alt="trusted_by_1millon_companies"
          fill
          // src={getAssetSrc('/utils-assets/product-description-trusted-logos.png')}
          src={getAssetSrc('/utils-assets/social-logos.png')}
          priority
          loader={customImageLoader}
          tw="object-contain"
        />
      </div>
    </div>
  );
};

export const HowItWorksStepsSection = () => {
  const { t } = useTranslation('product-description-page');

  const steps = [
    {
      Icon: ChatBubbleBottomCenterTextIcon,
      title: t('how_it_works_steps_step_1_title'),
      description: t('how_it_works_steps_step_1_description'),
    },
    {
      Icon: Cog6ToothIcon,
      title: t('how_it_works_steps_step_2_title'),
      description: t('how_it_works_steps_step_2_description'),
    },
    {
      Icon: UserGroupIcon,
      title: t('how_it_works_steps_step_3_title'),
      description: t('how_it_works_steps_step_3_description'),
    },
  ];

  return (
    <div tw="flex flex-col gap-10">
      <div tw="flex flex-col md:flex-row p-5 items-start gap-6 md:gap-10 self-stretch rounded-2xl bg-purple-50 relative">
        {steps.map(({ Icon, title, description }, index) => (
          <div key={index} tw="flex flex-col items-center gap-2 flex-1 relative w-full">
            {React.createElement(Icon as React.ElementType, { className: 'w-6 h-6 text-purple-500' })}
            <div tw="flex flex-col items-start self-stretch">
              <div tw="w-full text-neutral-700 text-center font-semibold leading-7">{title}</div>
              <div tw="self-stretch text-neutral-700 text-center font-normal leading-7">{description}</div>
            </div>
            {index < steps.length - 1 && (
              <svg
                tw="hidden xl:block absolute top-2 pointer-events-none"
                style={{ left: 'calc(100% - 105px)', width: '237px' }}
                width="308"
                height="8"
                viewBox="0 0 308 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.5 3.5C0.223858 3.5 0 3.72386 0 4C0 4.27614 0.223858 4.5 0.5 4.5V4V3.5ZM307.854 4.35355C308.049 4.15829 308.049 3.84171 307.854 3.64645L304.672 0.464466C304.476 0.269204 304.16 0.269204 303.964 0.464466C303.769 0.659728 303.769 0.976311 303.964 1.17157L306.793 4L303.964 6.82843C303.769 7.02369 303.769 7.34027 303.964 7.53553C304.16 7.7308 304.476 7.7308 304.672 7.53553L307.854 4.35355ZM0.5 4V4.5H307.5V4V3.5H0.5V4Z"
                  fill="#6633BB"
                />
              </svg>
            )}
          </div>
        ))}
      </div>
      <div tw="flex py-5 justify-center items-center gap-2 self-stretch rounded-2xl bg-green-50">
        <div tw="text-neutral-700 text-center text-xl font-semibold leading-7">
          💡 {t('ninety_two_percent_of_clients_receive_applications_within_four_business_days')}
        </div>
      </div>
    </div>
  );
};

export const CustomerStoriesSection = () => {
  const { t } = useTranslation('product-description-page');

  return (
    <div tw="flex flex-col gap-4">
      <ProductDescriptionSectionTitle title={t('every_customer_has_a_story')} />
      {productReviews.map((review, index) => (
        <ProductReviewCard key={index} {...review} />
      ))}
    </div>
  );
};

export const ContactSection = () => {
  const setShowContactRestworldManagerModal = useSetAtom(showContactRestWorldManagerModalAtom);

  const { t } = useTranslation('product-description-page');
  return (
    <div tw="px-[60px] py-10 bg-purple-50 rounded-2xl flex-col justify-center items-center inline-flex overflow-hidden">
      <Title
        textColor="neutral-700"
        fontWeight="semibold"
        content={t('still_have_doubts')}
        twStyle={tw`self-stretch text-center text-lg`}
      />
      <Title
        textColor="neutral-700"
        fontWeight="semibold"
        content={t('book_a_call_with_one_of_our_experts')}
        twStyle={tw`self-stretch text-center text-lg mb-8 relative -top-2`}
      />
      <ButtonV2
        onClick={() => setShowContactRestworldManagerModal(true)}
        StartIcon={HeadphoneIcon}
        variant="secondary-restworld"
      >
        {t('contact_support')}
      </ButtonV2>
    </div>
  );
};

const faqs = [
  {
    question: 'Restworld verifica le competenze e le esperienze dei candidati?',
    answer:
      'Sì, tutti i candidati vengono sottoposti a un rigoroso processo di verifica che include la revisione delle esperienze lavorative, referenze e, presto, test specifici per personalità e soft skills. Rispetto alle job board online, che si basano solo sull’autoselezione dei candidati, Restworld utilizza algoritmi avanzati di screening automatizzato che riducono il tempo di selezione del 80%, garantendo una qualità superiore delle assunzioni.',
  },
  {
    question: 'Quali settori copre Restworld?',
    answer:
      'Restworld è specializzato nella ristorazione e nell’hospitality. A differenza delle piattaforme generiche come Indeed o InfoJobs, che hanno grandi database ma senza specificità di settore, Restworld garantisce un pool di candidati mirato e costantemente aggiornato. Per i grandi ristoranti, questo significa poter contare su personale con esperienza nella gestione di più sedi e con capacità di lavorare in team strutturati.',
  },
  {
    question: 'Quanto tempo ci vuole per trovare i candidati giusti?',
    answer:
      'Il tempo di ricerca varia in base alla posizione e alla disponibilità del personale nella tua area. Tuttavia, grazie alla nostra piattaforma ottimizzata, molti ristoranti trovano candidati qualificati in pochi giorni. Le agenzie tradizionali tendono ad avere processi più lenti per via del reclutamento manuale, mentre le job board online si limitano alla gestione passiva delle candidature senza screening avanzato.',
  },
  {
    question: 'In che modo Restworld può aiutare i miei locali a trovare personale qualificato?',
    answer:
      'Restworld utilizza un sistema avanzato di matching che connette il tuo ristorante con candidati verificati e in linea con i tuoi standard operativi. Grazie alla nostra piattaforma, puoi coprire rapidamente le necessità di più sedi, mantenendo un livello costante di qualità del personale a prezzi calmierati.',
  },
  {
    question: 'Perché Restworld?',
    answer:
      'A differenza delle altre soluzioni, Restworld offre:<li>✅ Un Customer Success Specialist dedicato per un servizio personalizzato, perfetto per chi gestisce più sedi<li>✅ Un pool di oltre 180.000 candidati verificati con esperienza nella ristorazione strutturata<li>✅ Pubblicazione su oltre 12 piattaforme, social e canali di nicchia<li>✅ Automazione del processo di screening per ridurre drasticamente i tempi di selezione<li>✅ Flusso continuo di candidati, anziché una fornitura una tantum, ideale per ristoranti con più punti vendita',
  },
];

export const FaqSection = () => {
  return (
    <div tw="flex flex-col gap-12">
      <span tw="flex flex-row justify-center text-center">
        <Title fontSize="superLarge" fontWeight="semibold" textColor="neutral-700" content={'Domande frequenti'} />
      </span>
      <div tw="flex flex-col gap-4">
        {faqs.map((faq, index) => (
          <AccordionV2 key={index} tw="w-full" open={false}>
            <AccordionV2.Button tw="p-4 w-full rounded-lg text-left hover:bg-neutral-20">
              <div tw="pr-2">
                <Title fontWeight="semibold" content={faq.question} />
              </div>
            </AccordionV2.Button>
            <AccordionV2.Panel>
              {(() => {
                const content = faq.answer || '';
                const hasList = content.includes('<li>');
                if (hasList) {
                  const [lead, ...rest] = content.split('<li>');
                  const items = rest.map((s) => s.trim()).filter(Boolean);
                  return (
                    <div tw="p-4 mt-2 leading-6 bg-neutral-30 text-neutral-700 rounded-lg">
                      {lead && <div tw="mb-2">{lead}</div>}
                      {items.length > 0 && (
                        <ul tw="list-disc pl-5">
                          {items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                }
                return <div tw="p-4 mt-2 leading-6 bg-neutral-30 text-neutral-700 rounded-lg">{content}</div>;
              })()}
            </AccordionV2.Panel>
          </AccordionV2>
        ))}
      </div>
    </div>
  );
};

interface FunctionalityRowProps {
  noBottomBorder?: boolean;
  freeTier?: boolean;
  paidTier?: boolean;
  content?: string;
}

const FunctionalityRowWrapper = styled.div<{ noBottomBorder?: boolean }>`
  ${tw`flex font-medium text-base text-neutral-700 border-b py-6`}
  ${({ noBottomBorder }) => (noBottomBorder ? tw`border-b-0` : tw``)};
`;
